import TrainingService from './../../../shared/services/Training.service'
import TrainingTypeService from './../../../shared/services/TrainingType.service'
import TrainingAttendedService from './../../../shared/services/TrainingAttended.service'
import UserProfileService from './../../../shared/services/UserProfile.service'
import UserService from './../../../shared/services/User.service'
import { BehaviorSubject } from 'rxjs'
import UserTrainingQuizService from '../../../shared/services/UserTrainingQuiz.service'
import StorageService from './../../../shared/services/Storage.service'
import SystemSettingService from './../../../shared/services/SystemSetting.service'

const QUIZ_UNLOCK_WATCH_PERCENT = 95

const TrainingsSubject = new BehaviorSubject([])
const TrainingTypesSubject = new BehaviorSubject([])
const TrainingAttendedSubject = new BehaviorSubject([])
const ViewVideoSubject = new BehaviorSubject(null)
const EditVideoSubject = new BehaviorSubject(null)
const EditTypeSubject = new BehaviorSubject(null)
const UserTrainingQuizSubject = new BehaviorSubject([])
const RequiredTrainingsSubject = new BehaviorSubject(null)

const fetchTrainings = () => {
  TrainingService.search({
    pagination: false,
    order_by: { training_order: 'ASC', training_name: 'ASC' },
  })
    .then(trainings => TrainingsSubject.next(trainings && trainings?.models))
    .catch(error => {
      throw error
    })

  return TrainingsSubject
}

const fetchTrainingTypes = () => {
  TrainingTypeService.search({
    pagination: false,
    order_by: { type_order: 'ASC', training_type_name: 'ASC' },
  })
    .then(types => TrainingTypesSubject.next(types && types?.models))
    .catch(error => {
      throw error
    })

  return TrainingTypesSubject
}

const fetchTrainingAttended = userId => {
  userId = userId ? userId : UserProfileService.getUserId()
  TrainingAttendedService.search({
    pagination: false,
    search: { user_id: userId },
  })
    .then(attended => TrainingAttendedSubject.next(attended && attended?.models))
    .catch(error => {
      throw error
    })

  return TrainingTypesSubject
}

const getApplicableReqTypes = () => {
  const reqTypes = []

  if (UserProfileService.isA('signature-group')) reqTypes.push('sig_agent')

  const isLeader =
    UserProfileService.isA('recruiter-group') &&
    !UserProfileService.isA(['agency-owner', 'system-admin', 'affiliate-leader', 'associate-leader', 'premier-agent'])

  const isPremierAgentWithLeaderReqs =
    UserProfileService.isA('premier-agent') && parseInt(UserProfileService.get('has_leader_requirements')) === 1

  if (isLeader || isPremierAgentWithLeaderReqs) reqTypes.push('leader')

  return reqTypes
}

const fetchRequiredTrainings = async userId => {
  const reqTypes = getApplicableReqTypes()

  if (!reqTypes.length) {
    RequiredTrainingsSubject.next({ ids: [], trainings: [] })
    return RequiredTrainingsSubject
  }

  userId = userId ? userId : UserProfileService.getUserId()

  try {
    const [completed, outstanding] = await Promise.all([
      UserService.hasCompletedTrainings(userId),
      UserService.hasCompletedTrainings(userId, { expand: true }),
    ])

    const trainings = reqTypes
      .filter(reqType => !completed?.[reqType]?.current)
      .reduce((acc, reqType) => acc.concat(Array.isArray(outstanding?.[reqType]) ? outstanding[reqType] : []), [])
      .filter(
        (training, idx, self) =>
          training && training?.id && self.findIndex(t => parseInt(t?.id) === parseInt(training.id)) === idx
      )

    RequiredTrainingsSubject.next({
      ids: trainings.map(training => parseInt(training.id)),
      trainings,
    })
  } catch (ex) {
    console.error('Failed to fetch required trainings in TrainingsPage. ', ex)
    RequiredTrainingsSubject.next({ ids: [], trainings: [] })
  }

  return RequiredTrainingsSubject
}

const setViewVideo = training => ViewVideoSubject.next(training)

const setEditVideo = training => EditVideoSubject.next(training)

const setEditType = type => EditTypeSubject.next(type)

const submitCode = async (trainingId, code) => {
  let training
  try {
    training = await TrainingService.search({
      search: { id: trainingId, training_code: code },
    })
    training = training && training?.models ? training?.models : null
  } catch (ex) {
    console.error('Failed to fetch trainings in TrainingsPage. ', ex)
  }

  if (!training || !training.length) throw new Error('Invalid code supplied.')

  let attended = await TrainingAttendedService.search({
    search: {
      training_id: trainingId,
      user_id: UserProfileService.getUserId(),
    },
  })
  attended = attended && attended?.models ? attended?.models : null
  if (attended && attended.length) return true

  // Requirements are cached per day; drop it so a completed training
  // releases the enrollment lock without waiting for a reload.
  StorageService.clear('user---required-alerts')

  return await TrainingAttendedService.store({
    training_id: trainingId,
    user_id: UserProfileService.getUserId(),
    training_code: code,
  })
}

// Mark a training as attended after its quiz is passed. Quiz-required trainings
// have no training_code, but trainings_attended.training_code is NOT NULL, so we
// store a sentinel ('QUIZ') when the training itself has no code.
const markAttendedByQuiz = async trainingId => {
  const userId = UserProfileService.getUserId()

  let attended = await TrainingAttendedService.search({
    search: { training_id: trainingId, user_id: userId },
  })
  attended = attended && attended?.models ? attended?.models : null
  if (attended && attended.length) return true

  let training = await TrainingService.search({ search: { id: trainingId } })
  training = training && training?.models ? training?.models.shift() : null

  StorageService.clear('user---required-alerts')

  return await TrainingAttendedService.store({
    training_id: trainingId,
    user_id: userId,
    training_code: training && training.training_code ? training.training_code : 'QUIZ',
  })
}

const createTraining = async data => {
  let results = TrainingService.store(data)
  if (results)
    window.setTimeout(() => {
      setEditVideo(false)
      fetchTrainings()
    }, 1000)
  return results
}

const updateTraining = async (id, data, reorder) => {
  let results = TrainingService.update(id, data)
  if (results) {
    window.setTimeout(async () => {
      setEditVideo(false)
      fetchTrainings()
    }, 1000)
  }
  return results
}

const deleteTraining = async id => {
  let results = TrainingService.delete(id)
  if (results)
    window.setTimeout(() => {
      setEditVideo(false)
      fetchTrainings()
    }, 1000)
  return results
}

const createType = async data => {
  let results = TrainingTypeService.store(data)
  if (results)
    window.setTimeout(() => {
      setEditType(false)
      fetchTrainingTypes()
    }, 1000)
  return results
}

const updateType = async (id, data) => {
  let results = TrainingTypeService.update(id, data)
  if (results)
    window.setTimeout(() => {
      setEditType(false)
      fetchTrainingTypes()
    }, 1000)
  return results
}

const deleteType = async id => {
  let results = TrainingTypeService.delete(id)
  if (results)
    window.setTimeout(() => {
      setEditType(false)
      fetchTrainingTypes()
    }, 1000)
  return results
}

const deleteAttended = async training_id => {
  const attended = (
    await TrainingAttendedService.search({
      search: { training_id, user_id: UserProfileService.getUserId() },
    })
  ).models.shift()
  if (attended && attended?.id) {
    await TrainingAttendedService.delete(attended?.id)

    // After Deletoing, Remove the deleted training
    // attendance record from the Training Attended BehaviorSubject.
    let prevAttended = TrainingAttendedSubject.getValue()
    TrainingAttendedSubject.next(
      (Array.isArray(prevAttended) ? prevAttended : []).filter(a => parseInt(a.id) !== parseInt(attended?.id))
    )

    return attended
  }
}

const deleteUserTrainingQuiz = async training_id => {
  const user_training_quiz = (
    await UserTrainingQuizService.search({
      search: {
        training_id: training_id,
        user_id: UserProfileService.getUserId(),
      },
    })
  ).models.shift()

  if (user_training_quiz && user_training_quiz?.id) {
    await UserTrainingQuizService.delete(user_training_quiz?.id)

    // After Deletoing, Remove the deleted training
    // attendance record from the Training Attended BehaviorSubject.
    let prev_user_training_quiz = UserTrainingQuizSubject.getValue()
    UserTrainingQuizSubject.next(
      (Array.isArray(prev_user_training_quiz) ? prev_user_training_quiz : []).filter(
        a => parseInt(a.id) !== parseInt(user_training_quiz?.id)
      )
    )

    return user_training_quiz
  }
}

const isVimeoPlayerLink = link => {
  try {
    const url = new URL(`${link || ''}`.trim())
    return url.protocol === 'https:' && url.hostname === 'player.vimeo.com' && /^\/video\/\d+/.test(url.pathname)
  } catch (ex) {
    return false
  }
}

// Played ranges may overlap (rewatched sections), so merge before summing.
const getPlayedSeconds = ranges =>
  (Array.isArray(ranges) ? [...ranges] : [])
    .sort((a, b) => a[0] - b[0])
    .reduce(
      ({ total, end }, [start, stop]) => ({
        total: total + Math.max(0, stop - Math.max(start, end)),
        end: Math.max(end, stop),
      }),
      { total: 0, end: 0 }
    ).total

const fetchQuizUnlockWatchPercent = async () => {
  try {
    let value = (
      await SystemSettingService.search({ search: { setting_key: 'trainings---quiz-unlock-watch-percent' } })
    )?.models?.[0]?.setting_value
    if (typeof value === 'string') value = JSON.parse(value)
    const percent = typeof value === 'string' || typeof value === 'number' ? Number(value) : NaN
    return Number.isFinite(percent) && percent >= 1 && percent <= 100 ? percent : QUIZ_UNLOCK_WATCH_PERCENT
  } catch (ex) {
    return QUIZ_UNLOCK_WATCH_PERCENT
  }
}

const TrainingsPageService = {
  getTrainings: () => TrainingsSubject,
  getTrainingTypes: () => TrainingTypesSubject,
  getTrainingAttended: () => TrainingAttendedSubject,
  getRequiredTrainings: () => RequiredTrainingsSubject,
  fetchTrainings: fetchTrainings,
  fetchTrainingTypes: fetchTrainingTypes,
  fetchTrainingAttended: fetchTrainingAttended,
  fetchRequiredTrainings: fetchRequiredTrainings,

  setViewVideo: setViewVideo,
  getViewVideo: () => ViewVideoSubject,

  setEditVideo: setEditVideo,
  getEditVideo: () => EditVideoSubject,

  setEditType: setEditType,
  getEditType: () => EditTypeSubject,

  submitCode: submitCode,
  markAttendedByQuiz: markAttendedByQuiz,

  createTraining: createTraining,
  updateTraining: updateTraining,
  deleteTraining: deleteTraining,

  createType: createType,
  updateType: updateType,
  deleteType: deleteType,
  deleteAttended: deleteAttended,
  deleteUserTrainingQuiz: deleteUserTrainingQuiz,
  UserTrainingQuizSubject: UserTrainingQuizSubject,
  getUserTrainingQuizes: () => UserTrainingQuizSubject,

  QUIZ_UNLOCK_WATCH_PERCENT: QUIZ_UNLOCK_WATCH_PERCENT,
  isVimeoPlayerLink: isVimeoPlayerLink,
  getPlayedSeconds: getPlayedSeconds,
  fetchQuizUnlockWatchPercent: fetchQuizUnlockWatchPercent,
}

export default TrainingsPageService
